import React from 'react';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { ProgramOrderLineItem } from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

const displayValue = (value: string | null | undefined) =>
	value?.trim() ? value.trim() : '—';

const formatDueDate = (value: string | null | undefined) =>
	value ? dayjs(value).format('MMM D, YYYY') : '—';

export interface ProgramLinkingLineItemsTableProps {
	lineItems: ProgramOrderLineItem[];
	value?: string[];
	onChange?: (selectedIds: string[]) => void;
	loading?: boolean;
}

/**
 * Selectable table of order line items that may share one program.
 * Works as a controlled Form field (`value` / `onChange`) or standalone.
 */
const ProgramLinkingLineItemsTable: React.FC<ProgramLinkingLineItemsTableProps> = ({
	lineItems,
	value = [],
	onChange,
	loading = false,
}) => {
	const columns: ColumnsType<ProgramOrderLineItem> = [
		{
			title: 'Part',
			dataIndex: 'name',
			key: 'name',
			ellipsis: true,
			render: (name: string) => name?.trim() || 'Unnamed part',
		},
		{
			title: 'Qty',
			dataIndex: 'quantity',
			key: 'quantity',
			width: 64,
		},
		{
			title: 'Material',
			dataIndex: 'material_name',
			key: 'material_name',
			ellipsis: true,
			render: displayValue,
		},
		{
			title: 'Surface Finish',
			dataIndex: 'finish_name',
			key: 'finish_name',
			ellipsis: true,
			render: displayValue,
		},
		{
			title: 'Due Date',
			dataIndex: 'shop_due_date',
			key: 'shop_due_date',
			width: 120,
			render: (_, record) =>
				formatDueDate(record.shop_due_date ?? record.required_part_ship_date),
		},
	];

	return (
		<Table
			size="small"
			rowKey="id"
			pagination={false}
			loading={loading}
			dataSource={lineItems}
			columns={columns}
			rowSelection={{
				selectedRowKeys: value,
				onChange: (keys) => onChange?.(keys as string[]),
			}}
			locale={{ emptyText: <Text type="secondary">No matching parts on this order</Text> }}
		/>
	);
};

export default ProgramLinkingLineItemsTable;
